import React, { ElementType, ComponentPropsWithoutRef } from 'react';
import cx from 'classnames';

import styles from './OptionItemBase.module.css';
import Box, { BoxStyleProps } from '../Box';

const DEFAULT_ELEMENT = 'div';

interface OptionItemBaseOwnProps<C> extends BoxStyleProps {
  as?: C;
  className?: string;
  borderless?: boolean;
  innerClassName?: string;
  leftSlot?: React.ReactNode;
  leftSlotClassName?: string;
  middleSlot?: React.ReactNode;
  middleSlotClassName?: string;
  rightSlot?: React.ReactNode;
  rightSlotClassName?: string;
  selected?: boolean;
  dataTest?: string;
}

type OptionItemBaseProps<C extends ElementType> = OptionItemBaseOwnProps<C> &
  Omit<ComponentPropsWithoutRef<C>, keyof OptionItemBaseOwnProps<C>>;

export default function OptionItemBase<
  C extends ElementType = typeof DEFAULT_ELEMENT
>({
  as,
  className,
  borderless,
  innerClassName,
  leftSlot,
  leftSlotClassName,
  middleSlot,
  middleSlotClassName,
  rightSlot,
  rightSlotClassName,
  selected,
  dataTest,
  ...otherProps
}: OptionItemBaseProps<C>): JSX.Element {
  const Component = as || DEFAULT_ELEMENT;

  return (
    <Box
      as={Component}
      className={cx(
        styles['base'],
        { [styles['base--borderless']]: borderless },
        { [styles['is-selected']]: selected },
        className
      )}
      data-test-id={dataTest}
      {...otherProps}
    >
      <div className={cx(styles['inner'], innerClassName)}>
        {leftSlot && (
          <div className={cx(styles['left-slot'], leftSlotClassName)}>
            {leftSlot}
          </div>
        )}
        {middleSlot && (
          <div className={cx(styles['middle-slot'], middleSlotClassName)}>
            {middleSlot}
          </div>
        )}
        {rightSlot && (
          <div className={cx(styles['right-slot'], rightSlotClassName)}>
            {rightSlot}
          </div>
        )}
      </div>
    </Box>
  );
}
